import { useNavigation } from 'app/hooks/navigation';
import { useIsExpired } from 'app/hooks/use-is-expired';
import { useAllAddresses } from 'app/network/address';
import { useCreateListingChannel } from 'app/network/chat/channel';
import {
  useOneListing,
  useOneListingDistance,
  useUpdateOneListing,
  useAddListingPictures,
  useDeleteListingPictures,
} from 'app/network/listing';
import { useMyProfile } from 'app/network/profile';
import { useUser } from 'app/network/user';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useForm } from 'app/hooks/form';
import { useScopedTranslation } from 'app/hooks/i18n';
import type { UpdateListingDTO, Listing } from 'app/types/listing';
import type { ImagePickerSuccessResult } from 'expo-image-picker';
import * as yup from 'yup';
import Share from 'react-native-share';
import { Event, track } from 'app/lib/analytics';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import type { SpecificRouteProp } from 'app/types/navigation';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { optimisticListingHeroPictureAtom, optimisticListingHeroPictureBeingUpdatedAtom } from 'app/atoms/ui';
import { useAtom, useSetAtom } from 'jotai';
import { requestedMessageFooterChannelAtom } from 'app/atoms';
import { useCreateOneLoanParticipation } from 'app/network/loan';
import Toast from 'react-native-toast-message';
import Toast2 from 'react-native-root-toast';
import { isMobileNative, useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import * as Clipboard from 'expo-clipboard';
import { FeatureFlag, getShareListingUrl, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { throwUnlessAborted } from 'app/utils/network-helpers';
import { MAX_LISTING_DUPLICATES } from 'app/utils/constants';
import { isListingAvailabilityRangeValid } from 'app/lib/listing';
import { navigationRef } from 'app/lib/root-navigation';
import {
  isCopyOnlyListingEdit,
  useListingSaveFeedback,
} from 'app/screens/shared/edit-listing/use-listing-save-feedback';

export type UpdateListingFormValues = Omit<UpdateListingDTO, 'picture' | 'status' | 'item'> & {
  item: { name: string };
  /** Not sent to the API (stripped in onSubmit), but required for Yup when() branches. */
  type?: Listing['type'];
};

export interface UseListingProps {
  route: SpecificRouteProp<'one-listing'> | SpecificRouteProp<'edit-listing'>;
}

export function useListing({ route }: UseListingProps) {
  const { t } = useScopedTranslation();
  const navigation = useNavigation();
  const isGuestWeb = useIsGuestWeb();
  const { isLargeUI } = useUISize();
  const requestMessageFooterChannel = useSetAtom(requestedMessageFooterChannelAtom);
  const userQuery = useUser();
  useMyProfile();
  useAllAddresses();

  const id = route.params.id;

  const [showActions, setShowActions] = useState(false);
  const updateListing = useUpdateOneListing(id);
  const deleteListing = useUpdateOneListing(id);
  const listingLocation =
    route.name === 'one-listing'
      ? {
          searchCenterLatitude: route.params.searchCenterLatitude,
          searchCenterLongitude: route.params.searchCenterLongitude,
          searchDistance: route.params.searchDistance,
        }
      : undefined;
  const listingQuery = useOneListing(
    id,
    'one-listing',
    { throwOnError: isGuestWeb ? false : throwUnlessAborted },
    listingLocation,
  );
  const authorize = useIsExpired();

  const onDeletePress = () => {
    setShowActions(false);
    deleteListing.mutate(
      { status: 'deleted' },
      {
        onSuccess: (listing) => {
          navigation.navigate('main', {
            screen: 'management',
            params: {
              screen: 'my-listings',
              params: { type: listing.type },
              initial: false,
            },
          });
        },
      },
    );
  };

  const isAuthor = listingQuery.data?.userId === userQuery.data?.id;

  const listingDistanceQuery = useOneListingDistance(id, {
    enabled: listingQuery.data && !isAuthor,
  });

  const {
    error: mutationError,
    isPending: isCreatingChannel,
    mutate: mutateChannel,
  } = useCreateListingChannel({
    onSuccess: (channel) => {
      if (isLargeUI && navigationRef.isReady()) {
        const listingState = navigation.getState();
        const rootState = navigationRef.getRootState();
        const activeRootRoute = rootState?.routes[rootState.index];
        const previousListingRoute = listingState.routes[listingState.index - 1];
        const backRoute =
          previousListingRoute ??
          (rootState && activeRootRoute?.name === 'listingModal' ? rootState.routes[rootState.index - 1] : undefined);
        const tabState = backRoute?.name === 'main' ? backRoute.state : undefined;
        const activeTab = tabState?.routes[tabState.index ?? 0];
        const billboardState = activeTab?.name === 'billboard' ? activeTab.state : undefined;
        const returnScreen = billboardState ? billboardState.routes[billboardState.index ?? 0]?.name : backRoute?.name;

        // If we are in the landing or the billboard, we don't want to lose the context and thus stay inside those screens
        // goBack() will dismiss the opened modal on largeUI and open the message footer for the user we want to contact.
        if ((returnScreen === 'landing-page' || returnScreen === 'all-listings') && navigation.canGoBack()) {
          requestMessageFooterChannel(channel);
          navigation.goBack();
        } else {
          // Otherwise, navigate inside the chat tab screen.
          if (navigation.canGoBack()) navigation.goBack();
          navigationRef.navigate('main', {
            screen: 'chat',
            params: { screen: 'one-channel', params: { url: channel.channel_url }, initial: false },
          });
        }
        return;
      }
      return navigateToChat(navigation, channel.channel_url);
    },
  });

  function contactButtonHandler() {
    const listing = listingQuery.data;
    if (!listing) return;
    return listing.type === ListingType.offer ? authorize(() => mutateChannel(listing)) : mutateChannel(listing);
  }

  const createParticipation = useCreateOneLoanParticipation();

  function participateButtonHandler(numParticipants: number) {
    const listing = listingQuery.data;
    if (!listing) return;
    createParticipation.mutate(
      { listingId: listing.id, numParticipants },
      {
        onSuccess: () => {
          const action = numParticipants === 0 ? 'Deleted' : 'Saved';
          Toast.show({
            type: 'success',
            position: 'bottom',
            bottomOffset: 108,
            text1: t(`subscriptionsSection.participations${action}Toast.title`, {
              ns: 'one-listing',
              count: numParticipants,
            }),
            text2: t(`subscriptionsSection.participations${action}Toast.text`, { ns: 'one-listing' }),
            visibilityTime: 10000,
          });
        },
      },
    );
  }

  const setOptimisticListingHeroPicture = useAtom(optimisticListingHeroPictureAtom)[1];
  const setOptimisticListingHeroPictureBeingUpdated = useAtom(optimisticListingHeroPictureBeingUpdatedAtom)[1];

  // const addPicture = useAddListingPicture(id);
  const addManyPictures = useAddListingPictures(id);
  // const deletePicture = useDeleteListingPicture(id);
  const deletePictures = useDeleteListingPictures(id);
  const picturesChanged = useRef(false);

  const onImageChange = (result: ImagePickerSuccessResult, deletedIds?: string[]) => {
    if (result.assets.length > 0 || deletedIds?.length) picturesChanged.current = true;
    setOptimisticListingHeroPicture(result.assets[0]?.uri);
    setOptimisticListingHeroPictureBeingUpdated(id);
    const assets = result.assets;
    if (assets.length === 0) {
      if (deletedIds?.length) {
        deletePictures.mutate(deletedIds);
      }
      return;
    }
    addManyPictures.mutate(assets, {
      onSuccess: () => {
        if (deletedIds?.length) {
          deletePictures.mutate(deletedIds);
        }
      },
      onSettled: () => {
        setOptimisticListingHeroPicture(undefined);
        setOptimisticListingHeroPictureBeingUpdated(undefined);
      },
    });
  };

  const schema = useListingValidationSchema();

  const form = useForm({
    schema,
  });

  const [isFormInvalid, setIsFormInvalid] = useState(false);
  const saveFeedback = useListingSaveFeedback();
  const initialFormValues = form.formState.defaultValues;

  const onSubmit: ReturnType<typeof form.handleSubmit> = (...args) =>
    form.handleSubmit(
      (f) => {
        const { type: _type, ...data } = f as UpdateListingDTO & { type?: Listing['type'] };
        const copyOnly = !picturesChanged.current && isCopyOnlyListingEdit(form.getValues(), initialFormValues);
        updateListing.mutate(data, {
          onSuccess: (listing, variables) => saveFeedback.onSuccess(listing, variables, copyOnly),
          onError: saveFeedback.onError,
        });
      },
      (e) => {
        setIsFormInvalid(true);
        saveFeedback.onInvalid();
        console.log('[onSubmit.onInvalid]:', e);
      },
    )(...args);

  const resetFormFromListing = useEffectEvent(() => {
    if (listingQuery.data) {
      form.reset({
        type: listingQuery.data.type,
        subtype: listingQuery.data.subtype,
        start: listingQuery.data.start,
        end: listingQuery.data.end,
        addressIds: (listingQuery.data.addresses ?? []).map(({ id: mId }) => mId),
        categoryPrimaryId: listingQuery.data.categoryPrimary.id,
        serviceCategoryId: listingQuery.data.serviceCategory?.id ?? null,
        description: listingQuery.data.description,
        groupIds: (listingQuery.data.groups ?? []).map(({ id: mId }) => mId),
        customDistance: listingQuery.data.type === ListingType.offer ? null : listingQuery.data.customDistance,
        customCenterLatitude:
          listingQuery.data.type === ListingType.offer ? null : listingQuery.data.customCenterLatitude,
        customCenterLongitude:
          listingQuery.data.type === ListingType.offer ? null : listingQuery.data.customCenterLongitude,
        item: {
          name: listingQuery.data.item.name,
        },
        quantity: listingQuery.data.quantity,
        estimatedPrice: listingQuery.data.estimatedPrice,
        price: listingQuery.data.price,
        inventory: listingQuery.data.inventory,
        transactionMode: listingQuery.data.transactionMode,
        urgent: listingQuery.data.urgent ?? false,
        before: listingQuery.data.before,
        loanFee: listingQuery.data.loanFee,
        loanFeeDescription: listingQuery.data.loanFeeDescription,
        loanFeePaymentMethod: listingQuery.data.loanFeePaymentMethod,
        loanFeeDocument: listingQuery.data.loanFeeDocument ?? null,
        timeStart: listingQuery.data.timeStart,
        timeEnd: listingQuery.data.timeEnd,
        maxParticipants: listingQuery.data.maxParticipants ? listingQuery.data.maxParticipants.toFixed(0) : null,
        address: listingQuery.data.address,
        automaticMessage:
          listingQuery.data.type === ListingType.request ? listingQuery.data.automaticMessage : undefined,
        conciergerieDisabledByUser: listingQuery.data.conciergerieDisabledByUser,
        duplicates: null,
      });
    }
  });
  const listingLoaded = Boolean(listingQuery.data);
  const listingCustomDistance = listingQuery.data?.customDistance;
  useEffect(() => {
    if (listingLoaded) resetFormFromListing();
  }, [listingCustomDistance, listingLoaded]);

  useEffect(() => {
    form.setValue('before', listingQuery.data?.before);
  }, [form, listingQuery.data?.before]);

  const onSharePress = async () => {
    const listing = listingQuery.data;
    if (!listing) return;
    const url = getShareListingUrl(listing.id);
    if (isMobileNative) {
      const action = await Share.open({
        title: `Partage.club - ${listing.item.name}`,
        url,
      }).catch(() => ({ message: 'cancel' }));
      track(Event.ShareAction, { action: action.message });
    } else {
      // Platform = web
      await Clipboard.setStringAsync(url);
      Toast2.show(t('urlCopied', { ns: 'one-listing' }), {
        duration: Toast2.durations.SHORT,
      });
      track(Event.ShareAction, { action: 'web.CopyToPasteboard' });
    }
  };

  return {
    createChannel: {
      error: mutationError,
      isPending: isCreatingChannel,
      mutate: mutateChannel,
    },
    contactButtonHandler,
    form,
    isAuthor,
    isFormInvalid,
    listingDistanceQuery,
    listingQuery,
    navigation,
    onDeletePress,
    onImageChange,
    onSharePress,
    onSubmit,
    setIsFormInvalid,
    setShowActions,
    showActions,
    t,
    updateListing,
    deleteListing,
    userQuery,
    routeParams: route.params,
    participateButtonHandler,
    createParticipation,
  };
}

export function useListingValidationSchema() {
  const { data: user } = useUser();
  return yup.object().shape(
    {
      type: yup.mixed<Listing['type']>().optional(),
      subtype: yup.mixed<Listing['subtype']>().required(),
      addressIds: yup.array(yup.string().uuid().required()).required(),
      categoryPrimaryId: yup.string().uuid().required(),
      serviceCategoryId: yup.string().uuid().nullable().defined(),
      description: yup
        .string()
        .max(2000)
        .when('type', {
          is: (t: Listing['type']) => t === ListingType.request,
          then: (s) => s.min(3).required(),
          otherwise: (s) =>
            s
              .transform((v) => (v === '' ? null : v))
              .nullable()
              .defined(),
        }),
      groupIds: yup.array(yup.string().uuid().required()).required(),
      customDistance: yup
        .number()
        .nullable()
        .when('type', {
          is: (t: Listing['type']) => t === ListingType.request,
          then: (s) => s.required(),
        }),
      customCenterLatitude: yup
        .number()
        .nullable()
        .when('type', {
          is: (t: Listing['type']) => t === ListingType.request,
          then: (s) => s.required(),
        }),
      customCenterLongitude: yup
        .number()
        .nullable()
        .when('type', {
          is: (t: Listing['type']) => t === ListingType.request,
          then: (s) => s.required(),
        }),
      item: yup.object({
        name: yup.string().min(3).max(64).nullable().required(),
      }),
      start: yup.string().defined().nullable(),
      end: yup
        .string()
        .defined()
        .nullable()
        .test('end-after-start', 'new.dateSection.endBeforeStart', (end, context) => {
          const { start } = context.parent as Pick<UpdateListingFormValues, 'start'>;
          return isListingAvailabilityRangeValid(start, end);
        }),
      before: yup.string().defined().nullable(),
      quantity: yup.number().min(1).nullable().defined(),
      estimatedPrice: yup.number().nullable().defined(),
      price: yup
        .string()
        .nullable()
        .defined()
        .when(['type', 'transactionMode'], {
          is: (type: ListingType, mode?: TransactionMode | null) =>
            isWhiteLabel(WhiteLabel.HauteGaspesie) && type === ListingType.offer && mode === TransactionMode.sell,
          then: (priceSchema) => priceSchema.required(),
        }),
      inventory: yup.string().nullable().defined(),
      transactionMode: yup.mixed<TransactionMode>().nullable().defined(),
      urgent: yup
        .boolean()
        .defined()
        .test('urgent-requires-expirable', 'new.urgentSection.urgentRequiresExpirable', (urgent, context) => {
          const { subtype } = context.parent as Pick<UpdateListingFormValues, 'subtype'>;
          if (!urgent) return true;
          return subtype === ListingSubtype.expirable;
        }),
      loanFee: yup.number().nullable().defined(),
      loanFeeDescription: yup.string().nullable().defined(),
      loanFeePaymentMethod: yup.string().nullable().defined(),
      loanFeeDocument: yup.string().nullable().optional(),
      timeStart: yup.string().nullable().defined(),
      timeEnd: yup.string().nullable().defined(),
      address: yup
        .object({
          administrativeArea: yup.string().nullable().defined(),
          city: yup.string().nullable().defined(),
          country: yup.string().nullable().defined(),
          postalCode: yup.string().required(),
          street: yup.string().nullable().defined(),
          unit: yup.string().nullable().defined(),
        })
        .nullable()
        .defined(),
      maxParticipants: yup.string().nullable().defined(),
      automaticMessage: isFeatureEnabled(FeatureFlag.conciergerie, user?.featureFlags)
        ? yup.string().when('type', {
            is: (t: Listing['type']) => t === ListingType.request,
            then: (s) => s.required(),
            otherwise: (s) => s.optional(),
          })
        : yup.string().optional(),
      conciergerieDisabledByUser: yup.boolean().optional(),
      duplicates: yup.number().min(0).max(MAX_LISTING_DUPLICATES).integer().nullable().defined(),
    },
    [['addressIds', 'groupIds']],
  ) as yup.ObjectSchema<UpdateListingFormValues>;
}
